import React, { useEffect } from "react";
import { routes, loginRoutes } from "./routes";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { Box, Snackbar } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import Alert from "@mui/material/Alert";
import { showModal, showToast } from "../store/slices/common";
import Modal from "../components/Modal";
import { getModalProps } from "../components/Modal/Modalutils";
import { getAccessToken } from "../utils/auth-token-utils/cookie";
import Sidebar from "../components/SidebarComponent";
import MobileAppBar from "../components/MobileAppBar";
import Spinner from "../components/Spinner";
import { useSidebar } from "../context/SidebarContext";

function AuthenticatedRoutes() {
  const { contentMargin, isCollapsed, isMobile } = useSidebar();
  const location = useLocation();
  // Check if current route is the print route
  const isPrintRoute = location.pathname.startsWith("/calendar/print");

  // For print route, render without sidebar
  if (isPrintRoute) {
    return (
      <Box
        sx={{
          margin: 0,
          padding: 0,
          width: "100%",
          height: "100vh",
          boxSizing: "border-box",
        }}
      >
        <Routes>
          {loginRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={route.element()}
            />
          ))}
        </Routes>
      </Box>
    );
  }

  // Horizontal padding around the page content. On mobile the sidebar is an
  // overlay drawer, so the content takes the full width with tight gutters.
  const contentPadding = isMobile
    ? { xs: "12px", sm: "16px" }
    : isCollapsed
    ? { md: "15px 60px", lg: "15px 80px", xl: "15px 120px" }
    : { md: "15px 30px", lg: "15px 30px", xl: "15px 60px" };

  return (
    <Box
      sx={{
        margin: 0,
        // The sidebar is `position: fixed`, so a left margin equal to its
        // width keeps the content clear of it. A block box with no explicit
        // width naturally fills the remaining space, so `width` isn't needed.
        marginLeft: contentMargin,
        minWidth: 0,
        minHeight: "100vh",
        boxSizing: "border-box",
        transition: "margin-left 0.3s ease, padding 0.3s ease",
      }}
    >
      <Sidebar />
      <MobileAppBar />
      <Box
        sx={{
          padding: contentPadding,
          boxSizing: "border-box",
          minWidth: 0,
          transition: "padding 0.3s ease",
        }}
      >
        <Routes>
          {loginRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={route.element()}
            />
          ))}
        </Routes>
      </Box>
    </Box>
  );
}

const getRoutes = (accessToken) => {
  if (accessToken) {
    return <AuthenticatedRoutes />;
  } else
    return (
      <Routes>
        {routes.map((route) => (
          <Route key={route.path} path={route.path} element={route.element()} />
        ))}
      </Routes>
    );
};

export default function RootView() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const locationState = useLocation();
  const token = getAccessToken();

  const { toaster, modal, loader } = useSelector((state) => state.common);

  const accessToken = locationState?.state?.token || token;

  // Show "Session expired" toast when redirected here after token expiry (no re-render with cleared token = no blank screen)
  useEffect(() => {
    if (accessToken) return;
    const params = new URLSearchParams(locationState.search);
    if (params.get("session_expired") === "1") {
      dispatch(
        showToast({
          color: "info",
          msg: "Session expired. Please log in again.",
        })
      );
      navigate("/", { replace: true });
    }
  }, [accessToken, dispatch, navigate, locationState.search]);

  const closeToast = () => {
    setTimeout(() => {
      dispatch(showToast(null));
    }, 500);
  };

  const handleModalClose = () => {
    dispatch(showModal(null));
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={Boolean(toaster)}
        autoHideDuration={4000}
        onClose={closeToast}
      >
        {toaster && (
          <Alert onClose={closeToast} severity={toaster.color}>
            {toaster.msg}
          </Alert>
        )}
      </Snackbar>
      {loader && <Spinner />}
      {Boolean(modal) && (
        <Modal
          open={Boolean(modal)}
          handleClose={handleModalClose}
          {...getModalProps(modal)}
        />
      )}
      {getRoutes(accessToken)}
    </>
  );
}
